/**
 * usePolling(fetchFn, opts) — generic ~5s polling hook (WSRPDM-0013, used
 * here and by WSRPDM-0017). Fetch immediately, then every `intervalMs`; skip
 * the tick while `document.hidden`; fetch immediately on becoming visible
 * again; clear the interval + listener on unmount; never let two fetches
 * overlap. No websockets — this is the whole live-update mechanism.
 *
 * `fetchFn` is read through a ref, NOT the interval effect's dependency
 * array. Every screen call site passes an inline closure (`() =>
 * getSummary()`) or a fresh function on every render unless the caller
 * memoises it — depending on its identity would tear the interval down and
 * restart it (re-firing the immediate fetch) on every render. This is the
 * same class of bug WSRPDM-0012's `useTenantRoster` hit with `useCopy()`'s
 * `t` in a fetch effect's deps, except worse here: an unstable dep on a
 * *polling* hook is an unbounded-frequency request loop against a live
 * backend, not just a one-time refetch storm.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UsePollingOptions {
  intervalMs: number;
  /** Skip ticks while the tab is hidden, and fetch immediately on becoming visible. Default true. */
  pauseWhenHidden?: boolean;
}

export interface UsePollingResult<T> {
  data: T | null;
  error: unknown;
  /** True only until the first fetch settles — background poll ticks never re-arm it. */
  loading: boolean;
  /** Fetch now, outside the interval cadence (e.g. a manual Retry). */
  refresh: () => void;
}

export function usePolling<T>(
  fetchFn: () => Promise<T>,
  options: UsePollingOptions,
): UsePollingResult<T> {
  const { intervalMs, pauseWhenHidden = true } = options;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const fetchFnRef = useRef(fetchFn);
  // Sync the ref in an effect (not during render — mutating a ref value
  // while rendering is disallowed) — runs after every render, well before
  // any timer can fire, so `runFetch` always calls the latest `fetchFn`.
  useEffect(() => {
    fetchFnRef.current = fetchFn;
  });

  // Guards overlapping in-flight requests (a slow tick and the next timer
  // firing before it resolves) and stale state writes after unmount.
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  const runFetch = useCallback(() => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    fetchFnRef
      .current()
      .then((result) => {
        if (!mountedRef.current) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!mountedRef.current) return;
        setError(err);
      })
      .finally(() => {
        inFlightRef.current = false;
        if (mountedRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    runFetch();

    const intervalId = setInterval(() => {
      if (pauseWhenHidden && document.hidden) return;
      runFetch();
    }, intervalMs);

    function handleVisibilityChange() {
      if (!document.hidden) runFetch();
    }
    if (pauseWhenHidden) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
      if (pauseWhenHidden) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [intervalMs, pauseWhenHidden, runFetch]);

  return { data, error, loading, refresh: runFetch };
}
